import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import ResumeUpload from '@/components/common/ResumeUpload';
import ProfilePhotoUpload from '@/components/common/ProfilePhotoUpload';
import { useOwnPhotoUrl } from '@/shared/hooks/useOwnPhotoUrl';
import './ProfilePage.css';

function ProfilePage() {
    const { currentUser, userProfile, refreshProfile } = useAuth();
    const isStudent = userProfile?.roles?.includes('student');
    // Shown as soon as the server accepts a change, before the profile is
    // fetched again.
    const [resume, setResume] = useState(userProfile?.resume ?? null);
    useEffect(() => {
        setResume(userProfile?.resume ?? null);
    }, [userProfile?.resume]);
    const handleResumeChange = (next) => {
        setResume(next);
        refreshProfile();
    };

    const [photo, setPhoto] = useState(userProfile?.photo ?? null);
    useEffect(() => {
        setPhoto(userProfile?.photo ?? null);
    }, [userProfile?.photo]);
    const photoUrl = useOwnPhotoUrl(photo);
    const handlePhotoChange = (next) => {
        setPhoto(next);
        refreshProfile();
    };

    // The dashboard's "Upload Photo" link opens /profile#profile-photo. The
    // router does not scroll to a hash, so the page does: it brings the photo
    // section into view and puts focus on its heading.
    const { hash } = useLocation();
    const photoHeading = useRef(null);
    useEffect(() => {
        if (hash === '#profile-photo') {
            photoHeading.current?.scrollIntoView({ block: 'center' });
            photoHeading.current?.focus({ preventScroll: true });
        }
    }, [hash]);

    return (
        <Container className="py-4">
            <Row>
                <Col lg={8} className="mx-auto">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            {/*
                              Level is structural, the h4 class carries the
                              size. This is the page's title, and the page had
                              no h1 at all, so anyone navigating by heading had
                              nothing to land on.
                            */}
                            <h1 className="h4 mb-0">My Profile</h1>
                            <Button
                                as={Link}
                                to="/profile/edit"
                                variant="outline-primary"
                                size="sm"
                            >
                                Edit Profile
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={4} className="text-center mb-4">
                                    {photoUrl ? (
                                        <img
                                            src={photoUrl}
                                            alt={`Profile photo of ${userProfile?.name || currentUser?.email || 'user'}`}
                                            className="profile-avatar rounded-circle"
                                        />
                                    ) : (
                                        <div
                                            className="profile-avatar rounded-circle bg-secondary d-inline-flex align-items-center justify-content-center text-white"
                                            aria-label={`Profile avatar for ${userProfile?.name || currentUser?.email || 'user'}`}
                                        >
                                            {userProfile?.name?.[0]?.toUpperCase() ||
                                                currentUser?.email?.[0]?.toUpperCase()}
                                        </div>
                                    )}
                                </Col>
                                <Col md={8}>
                                    <h2 className="h5">
                                        {userProfile?.name || 'Name not set'}
                                    </h2>
                                    <p className="text-muted">
                                        {currentUser?.email}
                                    </p>

                                    {userProfile?.studentId && (
                                        <p>
                                            <strong>Student ID:</strong>{' '}
                                            {userProfile.studentId}
                                        </p>
                                    )}

                                    <div className="mb-3">
                                        <strong>Roles:</strong>
                                        <div className="mt-1">
                                            {userProfile?.roles?.map((role) => (
                                                <Badge
                                                    key={role}
                                                    bg="primary"
                                                    className="me-1"
                                                >
                                                    {role.replace('_', ' ')}
                                                </Badge>
                                            )) || (
                                                <span className="text-muted">
                                                    None assigned
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {userProfile?.major && (
                                        <p>
                                            <strong>Major:</strong>{' '}
                                            {userProfile.major}
                                        </p>
                                    )}

                                    {userProfile?.department && (
                                        <p>
                                            <strong>Department:</strong>{' '}
                                            {userProfile.department}
                                        </p>
                                    )}

                                    {userProfile && (
                                        <p>
                                            <strong>Optional email:</strong>{' '}
                                            {userProfile.preferences
                                                ?.emailNotifications === false
                                                ? 'Off'
                                                : 'On'}
                                        </p>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                    <Card
                        className="mt-4"
                        as="section"
                        id="profile-photo"
                        aria-labelledby="profile-photo-heading"
                    >
                        <Card.Body>
                            <h2
                                id="profile-photo-heading"
                                ref={photoHeading}
                                tabIndex={-1}
                                className="h5"
                            >
                                Profile photo
                            </h2>
                            <p className="text-muted small">
                                A photo helps others recognize you.
                            </p>
                            <ProfilePhotoUpload
                                id="profile-photo-upload"
                                photo={photo}
                                url={photoUrl}
                                onChange={handlePhotoChange}
                                name={userProfile?.name}
                            />
                        </Card.Body>
                    </Card>
                    {isStudent && (
                        <Card
                            className="mt-4"
                            as="section"
                            aria-labelledby="profile-resume-heading"
                        >
                            <Card.Body>
                                <h2 id="profile-resume-heading" className="h5">
                                    Resume
                                </h2>
                                <p className="text-muted small">
                                    Your resume doesn&apos;t need job
                                    experience. List the courses related to the
                                    positions you want, with grades if you like,
                                    and any tutoring, teaching or leadership
                                    you&apos;ve done. Upload it once. Every
                                    application you submit uses it and keeps its
                                    own copy of the file you sent, so changing
                                    it here never changes an application already
                                    submitted.
                                </p>
                                <ResumeUpload
                                    id="profile-resume"
                                    resume={resume}
                                    onChange={handleResumeChange}
                                    label="Add your resume"
                                />
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>
        </Container>
    );
}

export default ProfilePage;
